<template>
  <!--  -->
  <FsDialog v-model="select_template_popup" :close-modal="false" :class-name="popupClassName">
    <template #header>
      <div class="header_box">
        <div class="flex">
          <div class="header_left">Create topology diagram</div>
          <FsInput new-regular v-model="search" search placeholder="Search" clearable />
        </div>
        <span
          v-if="select_template_from_use_template"
          class="close_icon iconfont icon-a-Deleteshanchu"
          @click="handleClose"
        ></span>
      </div>
    </template>
    <div class="loading_box" v-show="dataLoading" v-loading="dataLoading"></div>
    <div class="ctn_box" v-if="!dataLoading && hasTemplateData">
      <div class="side_box">
        <FsCollapse v-model="activeCollapseKey" accordion>
          <!-- By Scenario 分类 -->
          <FsCollapseItem name="scenario">
            <template #title>
              <span class="category_title">By Scenario</span>
            </template>
            <div class="category_children">
              <div
                class="side_item"
                v-for="item in scenarioList"
                :key="item.typeId"
                :class="{ _active: tabActive === item.typeId && activeCategory === 'scenario' }"
                @click="handleCategoryItemClick('scenario', item.typeId)"
              >
                <span class="item_icon">
                  <span class="iconfont" :class="item.typeIcon || 'icon-template'"></span>
                </span>
                <span class="item_text" :title="item.typeName">{{ item.typeName }}</span>
              </div>
            </div>
          </FsCollapseItem>

          <!-- By Architecture 分类 -->
          <FsCollapseItem name="architecture">
            <template #title>
              <span class="category_title">By Architecture</span>
            </template>
            <div class="category_children">
              <div
                class="side_item"
                v-for="item in architectureList"
                :key="item.typeId"
                :class="{ _active: tabActive === item.typeId && activeCategory === 'architecture' }"
                @click="handleCategoryItemClick('architecture', item.typeId)"
              >
                <span class="item_icon">
                  <span class="iconfont" :class="item.typeIcon || 'icon-template'"></span>
                </span>
                <span class="item_text" :title="item.typeName">{{ item.typeName }}</span>
              </div>
            </div>
          </FsCollapseItem>
        </FsCollapse>
      </div>

      <div class="template_box" v-show="!search" v-loading="loading">
        <div
          v-if="!select_template_from_use_template"
          class="template_item template_item_create"
          @click.stop="
            handleTemplateClick({
              description: '',
              id: 0,
              name: '',
              picUrl: '',
            })
          "
        >
          <div class="topology_box">
            <span class="iconfont icon-fs_2020111802icon"></span>
          </div>
          <div class="template_info">
            <h4 title="Custom Solution">Custom Solution</h4>
            <p
              title="Create a project from scratch without a template. Customize the network architecture, configurations, and components to fit your needs."
            >
              Create a project from scratch without a template. Customize the network architecture, configurations, and
              components to fit your needs.
            </p>
          </div>
        </div>
        <div
          class="template_item template_item_exist"
          v-for="item in filterList"
          :key="item.id"
          @click.stop="handleTemplateClick(item)"
        >
          <div class="topology_box" :style="{ backgroundImage: `url(${item.picUrl})` }">
            <FsButton type="red" size="small">Create</FsButton>
          </div>
          <div class="template_info">
            <h4 :title="item.name">{{ item.name }}</h4>
            <p :title="item.description">{{ item.description }}</p>
          </div>
        </div>
      </div>

      <div class="template_box" v-show="search && filterList?.length" v-loading="loading">
        <div
          class="template_item template_item_exist"
          v-for="item in filterList"
          :key="item.id"
          @click.stop="handleTemplateClick(item)"
        >
          <div class="topology_box" :style="{ backgroundImage: `url(${item.picUrl})` }">
            <FsButton type="red" size="small">Create</FsButton>
          </div>
          <div class="template_info">
            <h4 :title="item.name">{{ item.name }}</h4>
            <p :title="item.description">{{ item.description }}</p>
          </div>
        </div>
      </div>

      <div class="no_data_box" v-show="search && !filterList?.length">
        <img src="https://resource.fs.com/mall/generalImg/20250509122213vjgf4w.svg" alt="" />
        <p class="no_data_title">Sorry, No Results Found for "{{ search }}"</p>
        <p class="no_data_info">You can use custom template by clicking the button</p>
        <FsButton
          type="red"
          size="mediumSmall"
          @click="
            handleTemplateClick({
              description: '',
              id: 0,
              name: '',
              picUrl: '',
            })
          "
          :loading="loading"
        >
          Use Custom Template
        </FsButton>
      </div>
    </div>
  </FsDialog>
</template>

<script setup lang="ts">
import { computed, ref, reactive, onMounted, watch } from 'vue'
import { FsDialog, FsButton, FsInput, FsCollapse, FsCollapseItem } from 'fs-design'
import { useHeaderStore } from '@/store/header'
import { useProjectStore } from '@/store/project'
import { storeToRefs } from 'pinia'
import { getTemplatePopupList, bindTemplate } from '@/api/modules/project'
import { useRouter } from 'vue-router'
import { useProductSelectionStore } from '@/store/productSelection'
import { trackEvent } from '@/utils/analytics'
import { debounce } from 'lodash-es'

// 定义模板项的类型
interface TemplateItem {
  description: string
  id: number
  name: string
  picUrl: string
}

// 定义每个类型项的类型
interface TypeItem {
  templateList: TemplateItem[]
  typeId: number
  typeName: string
  icon?: string
}

// 分类类型
type CategoryType = 'scenario' | 'architecture'

const productSelectionStore = useProductSelectionStore()
const projectStore = useProjectStore()
const headerStore = useHeaderStore()
const router = useRouter()
const search = ref<string>('')
const loading = ref<boolean>(false)
const dataLoading = ref<boolean>(false)
const { select_template_popup, select_template_from_use_template } = storeToRefs(headerStore)
// 从 store 获取 actions
const { setSelectedTemplateForUse, hideSelectTemplatePopup } = headerStore

const tabActive = ref<number>(0)
const activeCategory = ref<CategoryType>('scenario')

// 场景模板列表 (classify=1)
const scenarioList = ref<TypeItem[]>([])
// 架构模板列表 (classify=2)
const architectureList = ref<TypeItem[]>([])

// 折叠面板展开的 key
const activeCollapseKey = ref<CategoryType>('scenario')

// 是否有模板数据
const hasTemplateData = computed(() => {
  return scenarioList.value.length > 0 || architectureList.value.length > 0
})

const popupClassName = computed(() => {
  const currentList = activeCategory.value === 'scenario' ? scenarioList.value : architectureList.value
  const currentTypeItem = currentList.find(item => item.typeId === tabActive.value)
  if (currentTypeItem && currentTypeItem.templateList?.length > 5) {
    return 'select_template_popup select_template_popup_max'
  }
  return 'select_template_popup'
})

// 点击分类子项
const handleCategoryItemClick = (category: CategoryType, typeId: number) => {
  activeCategory.value = category
  tabActive.value = typeId
  activeCollapseKey.value = category
}

const handleTemplateClick = (item: TemplateItem) => {
  const currentProjectId = localStorage.getItem('currentProjectId')
  if (currentProjectId) {
    // 如果是从 "Use a template" 按钮打开的，通过 store 传递数据触发 designCenter 的弹窗
    if (select_template_from_use_template.value) {
      const currentList = activeCategory.value === 'scenario' ? scenarioList.value : architectureList.value
      const currentTypeItem = currentList.find(v => v.typeId === tabActive.value)

      if (currentTypeItem) {
        // 通过 store 设置选中的模板数据
        setSelectedTemplateForUse({
          id: item.id,
          typeId: currentTypeItem.typeId,
          name: item.name,
          description: item.description,
          picUrl: item.picUrl,
        })
        hideSelectTemplatePopup()
        search.value = ''
      }
      return
    }

    // 原有逻辑：直接绑定模板
    loading.value = true
    localStorage.setItem('currentTemplateTypeId', tabActive.value.toString())

    const currentList = activeCategory.value === 'scenario' ? scenarioList.value : architectureList.value
    const currentTypeName = currentList.find(v => v.typeId === tabActive.value)?.typeName

    bindTemplate({
      projectId: Number(currentProjectId),
      templateId: item.id,
      tempTypeId: tabActive.value,
    })
      .then(() => {
        trackEvent({
          eventCategory: 'des_project_create',
          eventAction: 'create_success',
          eventLabel: currentTypeName,
        })
        loading.value = false

        projectStore.setCurrentTemplateId(item.id)
        hideSelectTemplatePopup()
        search.value = ''
        projectStore.getAllProjectList()
        router.replace('/design-center')
        productSelectionStore.clearSelectedProductIds()
      })
      .catch(() => {
        loading.value = false
      })
  }
}

const debouncedTrack = debounce(() => {
  trackEvent({
    eventCategory: 'design_template',
    eventAction: 'template_operate',
    eventLabel: search.value,
  })
}, 1000)

// 关闭弹窗
const handleClose = () => {
  hideSelectTemplatePopup()
  search.value = ''
}

const filterList = computed(() => {
  const currentList = activeCategory.value === 'scenario' ? scenarioList.value : architectureList.value

  if (!search.value.trim()) {
    return currentList.find(item => item.typeId === tabActive.value)?.templateList || []
  }

  debouncedTrack()

  // 搜索时从两个列表中搜索
  const allTemplateList = [...scenarioList.value, ...architectureList.value].flatMap(item => item.templateList)
  return allTemplateList.filter(item => item.name.toLocaleLowerCase().includes(search.value.trim().toLocaleLowerCase()))
})

// 获取模板数据
const fetchTemplateData = async () => {
  dataLoading.value = true

  try {
    // 并行请求两个分类的数据
    const [scenarioRes, architectureRes] = await Promise.all([
      getTemplatePopupList({ classify: 1 }),
      getTemplatePopupList({ classify: 2 }),
    ])

    // 处理场景模板数据
    if (Array.isArray(scenarioRes.data) && scenarioRes.data.length > 0) {
      scenarioList.value = scenarioRes.data as TypeItem[]
      // 默认选中第一个场景模板
      tabActive.value = scenarioRes.data[0].typeId
      activeCategory.value = 'scenario'
    }

    // 处理架构模板数据
    if (Array.isArray(architectureRes.data) && architectureRes.data.length > 0) {
      architectureList.value = architectureRes.data as TypeItem[]
    }

    // 如果场景模板为空但架构模板有数据，则默认选中架构模板
    if (scenarioList.value.length === 0 && architectureList.value.length > 0) {
      tabActive.value = architectureList.value[0].typeId
      activeCategory.value = 'architecture'
      activeCollapseKey.value = 'architecture'
    }
  } catch (error) {
    console.error('Failed to fetch template list:', error)
  } finally {
    dataLoading.value = false
  }
}

onMounted(() => {
  fetchTemplateData()
})

watch(
  select_template_popup,
  (newVal: boolean) => {
    if (newVal) {
      // 弹窗打开时可以刷新数据（如果需要）
    }
  },
  { immediate: true }
)
</script>

<style lang="scss">
.fs-dialog {
  > .select_template_popup {
    width: calc(100% - 128px) !important;
    max-width: 1200px !important;

    &.select_template_popup_max {
      //   height: calc(100% - 128px);
    }
  }

  .select_template_popup {
    .fs-dialog__content--box {
      padding: 0;
      flex: 1 1 auto;
      min-height: 0;
    }
  }
}
</style>

<style lang="scss" scoped>
.header_box {
  padding: 19px 20px 19px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  border-bottom: 1px solid #eee;

  .flex {
    display: flex;
    align-items: center;
  }

  .header_left {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-right: 47px;
    font-weight: 600;
    @include font16;
    color: #19191a;
  }

  :deep(.fs-input) {
    width: 400px;
  }

  .close_icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    font-size: 16px;
    line-height: 1;
    color: #707070;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s;
    padding: 8px;
    box-sizing: content-box;

    &:hover {
      color: #19191a;
      background: rgba(25, 25, 26, 0.04);
    }
  }

  .iconfont {
    display: inline-block;
    width: 16px;
    height: 16px;
    font-size: 16px;
    line-height: 1;
    color: #707070;
    font-weight: 400;
    margin-left: 4px;
  }

  :deep(.fs-input) {
    &.is-focus {
      .fs-input__wrapper {
        border-color: #f6f6f8;
        background: #f6f6f8;
      }
    }

    .fs-input__wrapper {
      border-color: #f6f6f8;
      background: #f6f6f8;
    }
  }
}

.loading_box {
  height: 300px;
  position: relative;
}

.ctn_box {
  display: flex;
  flex-direction: row;
  height: 556px; // 固定高度，容纳两行模板不出现滚动条
  overflow: hidden;
  width: 100%;
}

.side_box {
  width: 240px;
  overflow-y: auto;
  flex-shrink: 0;
  background: #fff;
  padding: 16px;
  height: 100%;
  border-right: 1px solid #eee;
  box-sizing: border-box;

  :deep(.fs-collapse) {
    border: none;
    background: transparent;

    .fs-collapse-item {
      border-bottom: none;
      margin-bottom: 0;

      .fs-collapse-item__header {
        padding: 8px 12px;
        font-weight: 600;
        background: transparent;
        border-radius: 4px;
        border-bottom: none;
        height: auto;
        line-height: 1.5;

        &:hover {
          background: #f7f7f7;

          .fs-collapse-item__icon i {
            color: #19191a;
          }
        }

        &.is-active {
          .fs-collapse-item__icon i {
            color: #19191a;
          }
        }

        .category_title {
          @include font14;
          font-weight: 600;
          color: #19191a;
        }
      }

      .fs-collapse-item__icon {
        padding: 0;

        i {
          font-size: 12px;
          color: #707070;
          transition: color 0.2s ease;
        }
      }

      .fs-collapse-item__content {
        padding: 0;
        background: transparent;
      }
    }
  }

  .category_children {
    padding: 0;
  }

  .side_item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    position: relative;
    transition: background 0.2s ease;
    border-radius: 4px;

    &:hover {
      background: #f7f7f7;

      .item_icon .iconfont,
      .item_text {
        color: #19191a;
      }
    }

    &._active {
      background: #f7f7f7;

      .item_icon .iconfont,
      .item_text {
        color: #19191a;
      }

      .item_text {
        &:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          height: 2px;
          background-color: #c41230;
        }
      }
    }

    .item_icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      margin-right: 8px;
      flex-shrink: 0;

      .iconfont {
        font-size: 16px;
        color: #707070;
        transition: color 0.2s ease;
      }
    }

    .item_text {
      @include font14;
      color: #707070;
      // padding-bottom: 4px;
      position: relative;
      @include textOverflow();
      max-width: 170px;
      transition: color 0.2s ease;
    }
  }
}

.trigger_content {
  h4 {
    @include font13;
    margin-bottom: 4px;
    word-break: break-word;
    white-space: normal;
  }

  p {
    @include font12;
    word-break: break-word;
    white-space: normal;
  }
}

.template_box {
  align-items: flex-start;
  align-content: flex-start;
  flex-wrap: wrap;
  padding: 20px;
  display: flex;
  flex: 1 1 auto;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
  .template_item {
    margin-bottom: 24px;
    cursor: pointer;
    width: calc((100% - 40px) / 3);
    margin-right: 20px;
    flex-shrink: 0;
    position: relative;

    &:nth-child(3n) {
      margin-right: 0;
    }

    :deep(.fs-tooltip) {
      width: 100%;
      margin-bottom: 16px;
    }

    &.template_item_create {
      .topology_box {
        background: #fafafa;
      }
    }

    &.template_item_exist {
      &:hover {
        .topology_box {
          :deep(.fs-button) {
            display: inline-flex;
          }
        }
      }
    }

    &:hover {
      .topology_box {
        :deep(.fs-button) {
          display: inline-flex;
        }

        &:after {
          opacity: 0.04;
        }
      }

      .template_info {
        h4 {
          text-decoration: underline;
        }
      }
    }
  }

  .topology_box {
    width: 100%;
    height: 158px;
    position: relative;
    margin-bottom: 16px;
    align-items: center;
    justify-content: center;
    display: flex;
    background: #fafafa;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #eeeeee;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;

    &:after {
      display: block;
      content: ' ';
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      position: absolute;
      background-color: #19191a;
      opacity: 0;
      transition: all 0.3s;
      z-index: 1;
    }

    .iconfont {
      font-size: 20px;
      color: #707070;
    }

    :deep(.fs-button) {
      position: absolute;
      right: 8px;
      bottom: 8px;
      display: none;
      z-index: 2;
    }
  }

  .template_info {
    h4 {
      color: #19191a;
      @include font14;
      font-weight: 600;
      margin-bottom: 8px;
      @include textOverflow();
    }

    p {
      @include font12;
      color: #707070;
      @include textClampOverflow(2);
    }
  }
}

.no_data_box {
  padding: 68px 0 68px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;

  > img {
    display: inline-block;
    width: 120px;
    height: 69px;
  }

  .no_data_title {
    @include font14;
    font-weight: 600;
    color: #19191a;
    margin: 12px 0 4px 0;
  }

  .no_data_info {
    @include font12;
    color: #707070;
    margin-bottom: 16px;
  }

  :deep(.fs-button) {
    min-height: auto !important;
  }
}
</style>
